import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { Subject, Subscription } from "rxjs";

import { finalize, takeUntil } from "rxjs/operators";
import { userSortModes } from "../../../constants/user-list-filters";
import { CommunityFilter } from "../../../models/identity/community-filter.model";
import { Filters } from "../../../models/identity/filters.model";
import { User } from "../../../models/identity/user.model";
import { AccountsService } from "../../../services/identity/accounts.service";
import { ProfileService } from "../../../services/identity/profile.service";
import { CacheService } from "../../../services/shared/cache.service";

@Component({
    selector: 'app-widget-connect',
    templateUrl: './widget-connect.component.html',
    styleUrls: ['widget-connect.component.scss'],
})
export class WidgetConnectComponent implements OnInit, OnDestroy {
    private currentFilters: Filters | null = null;
    private destroy$ = new Subject<void>();
    private usersSub?: Subscription;

    public currentUser: User = this.accountsService.currentUser;
    public fetchedUsers: User[];
    public isLoading: boolean = false;
    public isProfileModalVisible: boolean = false;
    public isSearchContactModalVisible: boolean = false;
    public selectedUser: User | null = null;
    public sortMode = userSortModes.user;
    public users: User[] = [];

    constructor(
        private readonly profileService: ProfileService,
        private readonly accountsService: AccountsService,
        private readonly router: Router,
        private readonly cacheService: CacheService,
    ) {}

    public goToMyProfile() {
        this.router.navigate(['/profile/edit']);
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.usersSub?.unsubscribe();
    }

    public ngOnInit() {
        this.loadDefaultUsers();
    }

    public onSearchSubmit(filters: Filters | null) {
        this.currentFilters = filters;

        if (this.currentFilters === null) {
            this.users = [];
            this.isLoading = false;
        } else {
            this.loadUsersByFilters(this.currentFilters);
        }
    }

    public openProfileModal(user: User) {
        this.selectedUser = user;
        this.isProfileModalVisible = true;
    }

    public openSearchContactModal() {
        this.isSearchContactModalVisible = true;
    }

    public refreshUsers() {
        if (this.currentFilters === null) {
            this.users = [];
        } else {
            this.displayRandomUsers();
        }
    }

    private displayRandomUsers() {
        const randomUsers = [...this.fetchedUsers].sort(() => 0.5 - Math.random());
        this.users = randomUsers.slice(0, 10);
    }

    private generateCacheKey(filters: Filters): string {
        const filterKey = JSON.stringify({
            communities: filters.communities,
            skillsIds: filters.skillsIds,
            skillDomainsIds: filters.skillDomainsIds,
            interestsIds: filters.interestsIds,
            interestDomainsIds: filters.interestDomainsIds,
            businessAreasIds: filters.businessAreasIds,
            organizationsIds: filters.organizationsIds,
            geographicalAreasIds: filters.geographicalAreasIds,
            filterGeographicStrict: filters.filterGeoraphicStrict,
            isMyOrganizationChecked: filters.isMyOrganizationChecked,
            isAllCommunitiesChecked: filters.isAllCommunitiesChecked,
            userCustomSearch: filters.userCustomSearch,
        });
        return `widget-connect-profiles-${btoa(filterKey)}`;
    }

    private loadDefaultUsers() {
        this.isLoading = true;
        const user = this.accountsService.currentUser;
        const hasCommunities = user.communities && user.communities.length > 0;
        const hasSkills = user.skills && user.skills.length > 0;

        if (hasCommunities && hasSkills) {
            const defaultFilters = new Filters();
            defaultFilters.communities = user.communities.map((comm) => new CommunityFilter(comm.id, []));
            defaultFilters.skillsIds = user.skills.map((skill) => skill.id);
            defaultFilters.page = 1;
            defaultFilters.pageSize = 100;

            this.currentFilters = defaultFilters;
            this.loadUsersByFilters(this.currentFilters);
        } else {
            this.currentFilters = null;
            this.users = [];
            this.isLoading = false;
        }
    }

    private loadUsersByFilters(filters: Filters) {
        this.usersSub?.unsubscribe();
        this.isLoading = true;
        const currentUserId = this.accountsService.currentUser.userId;
        filters.sortMode = this.sortMode;

        const cacheKey = this.generateCacheKey(filters);
        const profilesObservable = this.profileService.getFilteredProfiles(filters);

        this.usersSub = this.cacheService
            .get(cacheKey, profilesObservable, this.cacheService.DEFAULT_MAX_AGE)
            .pipe(
                finalize(() => (this.isLoading = false)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (fetchedUsers) => {
                    this.fetchedUsers = fetchedUsers.filter((user) => user.userId !== currentUserId);
                    this.displayRandomUsers();
                },
                error: () => {
                    this.users = [];
                },
            });
    }
}
